import React from 'react';
import Hero from '../components/sections/Hero';
import Container from '../components/layout/Container';
// Card components available but not used on Home (custom card markup used instead)
import Button from '../components/common/Button';
import { useLanguage } from '../contexts/LanguageContext';
import { getAllDepartments } from '../data/departments';
import { getAllStaff } from '../data/staff';
import logo from '../assets/images/logo.jpg';
import './Home.css';

const Home = () => {
    const { t, language } = useLanguage();
    const departments = getAllDepartments();
    const staff = getAllStaff();

    const deptIcons = {
        'computer-science': 'computer',
        'oil-gas': 'local_gas_station',
        'management': 'corporate_fare',
        'accounting': 'account_balance',
    };

    return (
        <div className="home-page">
            <Hero />

            {/* About Snippet - matching example */}
            <section className="section bg-white" id="about">
                <Container>
                    <div className="about-intro-grid">
                        <div className="about-intro-images reveal reveal--left">
                            <div className="about-image-mosaic">
                                <img src={logo} alt="Da Vinci Institute" className="about-img about-img--offset" />
                            </div>
                        </div>
                        <div className="about-intro-content reveal reveal--right">
                            <h2 className="section-title">
                                {t('about.homeTitle').split(' ').map((word, i) =>
                                    i >= t('about.homeTitle').split(' ').length - 2
                                        ? <span key={i} className="text-primary">{word} </span>
                                        : word + ' '
                                )}
                            </h2>
                            <p className="text-lg leading-relaxed" style={{ color: 'var(--color-neutral-500)' }}>
                                {t('about.homeDesc')}
                            </p>
                            <ul className="about-checklist">
                                <li>
                                    <span className="material-icons about-check-icon">check_circle</span>
                                    <span className="font-medium">Industry-standard Laboratory Facilities</span>
                                </li>
                                <li>
                                    <span className="material-icons about-check-icon">check_circle</span>
                                    <span className="font-medium">Expert Faculty with Industry Backgrounds</span>
                                </li>
                                <li>
                                    <span className="material-icons about-check-icon">check_circle</span>
                                    <span className="font-medium">Job Placement Assistance Programs</span>
                                </li>
                            </ul>
                            <Button href="/about" variant="tertiary" className="about-learn-more">
                                {t('common.learnMore')}
                                <span className="material-icons" style={{ fontSize: '18px', marginLeft: '8px' }}>arrow_forward</span>
                            </Button>
                        </div>
                    </div>
                </Container>
            </section>

            {/* Departments Grid - matching example */}
            <section className="section" id="departments">
                <Container>
                    <div className="section-header text-center mb-16 reveal reveal--fade-in">
                        <h2 className="section-title">
                            {t('sections.departments').split(' ').map((word, i, arr) =>
                                i === arr.length - 1
                                    ? <span key={i} className="text-primary">{word}</span>
                                    : word + ' '
                            )}
                        </h2>
                        <p className="section-description">
                            Diverse programs designed to equip you with the skills demanded by today's evolving global market.
                        </p>
                    </div>
                    <div className="departments-grid">
                        {departments.map((dept, index) => (
                            <div key={dept.id} className={`reveal reveal--up delay-${(index + 1) * 100}`}>
                                <div className="dept-card">
                                    <div className="dept-card__icon-wrapper">
                                        <span className="material-icons dept-card__icon">
                                            {deptIcons[dept.slug] || 'school'}
                                        </span>
                                    </div>
                                    <h3 className="dept-card__title">{dept[language].shortTitle || dept[language].title}</h3>
                                    <p className="dept-card__desc">
                                        {dept[language].description.substring(0, 120)}...
                                    </p>
                                    <a href={`/departments/${dept.slug}`} className="dept-card__link">
                                        VIEW COURSES <span className="material-icons" style={{ fontSize: '14px', marginLeft: '4px' }}>chevron_right</span>
                                    </a>
                                </div>
                            </div>
                        ))}
                    </div>
                </Container>
            </section>

            {/* Staff Section - matching example */}
            <section className="section bg-primary-light" id="staff">
                <Container>
                    <div className="staff-header reveal reveal--fade-in">
                        <div>
                            <h2 className="section-title">
                                {t('sections.ourStaff').split(' ').map((word, i, arr) =>
                                    i === arr.length - 1
                                        ? <span key={i} className="text-primary">{word}</span>
                                        : word + ' '
                                )}
                            </h2>
                            <p className="text-muted">Expert instructors dedicated to your academic success.</p>
                        </div>
                    </div>
                    <div className="staff-grid">
                        {staff.map((member, index) => (
                            <div key={member.id} className={`reveal reveal--up delay-${(index % 4 + 1) * 100}`}>
                                <div className="staff-card">
                                    <div className="staff-card__image-wrapper">
                                        <img src={member.image} alt={member.name[language]} className="staff-card__image" />
                                    </div>
                                    <div className="staff-card__info">
                                        <h4 className="staff-card__name">{member.name[language]}</h4>
                                        <p className="staff-card__position">{member.position[language]}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Container>
            </section>

            {/* Contact CTA - matching example */}
            <section className="section">
                <Container>
                    <div className="cta-banner reveal reveal--scale">
                        <div className="cta-decorative" aria-hidden="true">
                            <div className="cta-circle cta-circle--1"></div>
                            <div className="cta-circle cta-circle--2"></div>
                        </div>
                        <div className="cta-content">
                            <h2>{t('sections.getInTouch')}</h2>
                            <p>Have questions about our programs or admissions? Our team is ready to help you start your journey.</p>
                        </div>
                        <div className="cta-actions">
                            <Button size="lg" href="/contact" variant="white">
                                {t('hero.contact')}
                            </Button>
                            <Button size="lg" href="/departments" variant="outline-white">
                                {t('hero.explore')}
                            </Button>
                        </div>
                    </div>
                </Container>
            </section>

            {/* Map */}
            <section className="section" style={{ paddingTop: 0 }}>
                <Container>
                    <div className="map-container animate-fade-in">
                        <iframe
                            title="Da Vinci Institute Location"
                            src="https://www.google.com/maps/embed/v1/place?q=Da+Vinci+Institute,+Duhok,+Iraq&key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8"
                            width="100%"
                            height="450"
                            style={{ border: 0 }}
                            allowFullScreen=""
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                        ></iframe>
                    </div>
                </Container>
            </section>
        </div>
    );
};

export default Home;
